import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';
import { VariableSchemaFormComponent } from '../../shared/modules/variable-schema-form/variable-schema-form.component';

export interface RunNowDialogData {
  routineName: string;
  useHeadDefault: boolean;
  variableSchema?: any;
  workflowName?: string;
  /** The routine's stored arguments, parsed (SCRUM-625). */
  routineArguments?: Record<string, unknown>;
}

export interface RunNowDialogResult {
  useHead: boolean;
  arguments?: string;
}

@Component({
  selector: 'app-run-now-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatDividerModule,
    VariableSchemaFormComponent,
  ],
  templateUrl: './run-now-dialog.component.html',
  styleUrls: ['./run-now-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RunNowDialogComponent {
  private dialogRef = inject(MatDialogRef<RunNowDialogComponent>);
  data = inject<RunNowDialogData>(MAT_DIALOG_DATA);

  useHead: boolean = this.data.useHeadDefault;

  /** Whether the variable schema form is valid (true when no schema present). */
  schemaValid = !this.data.variableSchema;

  /** Current form values from the variable schema form. */
  schemaValues: Record<string, unknown> = {};

  get hasSchema(): boolean {
    return !!this.data.variableSchema?.properties;
  }

  /** Whether the routine has stored arguments pre-filling the form. */
  readonly hasStoredArguments = Object.keys(this.data.routineArguments ?? {}).length > 0;

  /**
   * Initial form values from the routine's stored arguments, restricted to
   * keys present in the variable schema (SCRUM-625). Computed once — a stable
   * reference, so the OnPush schema form is not re-initialized on every
   * change-detection cycle.
   *
   * The server merges job arguments as
   * `schema defaults < routine.arguments < operator args`, and this form's
   * values are sent as operator args — so the form must start from the
   * effective lower layers or an untouched schema default would silently
   * override the routine's stored value.
   *
   * Keys not in the schema are intentionally excluded: the schema form never
   * serializes them (emitValues iterates only schema fields), and the server
   * merges them from routine.arguments anyway.
   */
  readonly schemaInitialValues = this.computeSchemaInitialValues();

  private computeSchemaInitialValues(): Record<string, unknown> | undefined {
    const routineArgs = this.data.routineArguments;
    const properties = this.data.variableSchema?.properties;
    if (!routineArgs || !properties) {
      return undefined;
    }
    const values: Record<string, unknown> = {};
    for (const key of Object.keys(routineArgs)) {
      if (key in properties) {
        values[key] = routineArgs[key];
      }
    }
    return Object.keys(values).length > 0 ? values : undefined;
  }

  onSchemaValuesChange(values: Record<string, unknown>): void {
    this.schemaValues = values;
  }

  onSchemaValidChange(valid: boolean): void {
    this.schemaValid = valid;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onRun(): void {
    const result: RunNowDialogResult = {
      useHead: this.useHead,
    };

    if (this.hasSchema && Object.keys(this.schemaValues).length > 0) {
      result.arguments = JSON.stringify(this.schemaValues);
    }

    this.dialogRef.close(result);
  }
}
